import React, { useEffect, useRef, useState } from "react";

// 🔹 State Management
import { RootState, useAppDispatch, useAppSelector } from "../../../../store";

// 🔹 UI Component Libraries
import { Checkbox, Input, Select, Spin, Modal } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";

// 🔹 Internal Components
import FullScreenLoader from "../../../Components/FullScreenLoader";
import NoDataMessage from "../../../Components/NoData";
import NoPermissionPage from "../../../Components/NoPermissionPage";
import NotificationButton from "../../../Components/NotificationButton";

// 🔹 Internal Utilities
import {
  CapitalizeFirstWord,
  formatToLocalDate,
  useDebounce,
} from "../../../../helpers/utils";
import axiosInstance from "../../../../helpers/axios";
import { responseDataFormate } from "../../../../helpers/bycrypt";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import { decryptByText } from "../../../../helpers/bycrypt";
import { GetCountryListAPI, GetPrefixListAPI } from "../../../Redux/country";
import {
  CreateDeliveryZoneAPI,
  UpdateDeliveryZoneAPI,
} from "../../../Redux/deliveryPricing";

// 🔹 Assets
import { svg } from "../../../../Assets/svg";
import { useNavigate, useParams } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import { BtnLoader } from "../../../../Components/Components";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { getParcelBoxesAPI, ParcelBoxItem } from "../parcelBoxApis";

interface ZoneData {
  zone_name: string;
  country_id: string;
  dispatch_to_delivery_allowance?: string | number;
  pallet_price: string | number;
  half_price: string | number;
  full_price: string | number;
  mezzanine_half_price: string | number;
  mezzanine_full_price: string | number;
  fork_lift_price: string | number;
  boom_lift_price: string | number;
  scissor_lift_price: string | number;
  area_codes: string[];
  box_prices?: { [key: string]: string | number };
}

const schema = Yup.object().shape({
  zone_name: Yup.string().required("Zone Name is required"),
  country_id: Yup.string().required("Country is required"),
  dispatch_to_delivery_allowance: Yup.number()
    .typeError("Dispatch to Delivery Allowance must be a number")
    .min(0, "Must be 0 or greater")
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value,
    ),
  pallet_price: Yup.number()
    .required("Pallet Price is required")
    .min(0, "Must be greater than 0"),
  half_price: Yup.number()
    .required("Half Load (Trailer) Price is required")
    .min(0, "Must be greater than 0"),
  full_price: Yup.number()
    .required("Full Load (Trailer) Price is required")
    .min(0, "Must be greater than 0"),
  mezzanine_half_price: Yup.number()
    .required("Mezzanine Half Load (Trailer) Price is required")
    .min(0, "Must be greater than 0"),
  mezzanine_full_price: Yup.number()
    .required("Mezzanine Full Load (Trailer) Price is required")
    .min(0, "Must be greater than 0"),
  fork_lift_price: Yup.number()
    .required("Fork Lift Transport Price is required")
    .min(0, "Must be greater than 0"),
  boom_lift_price: Yup.number()
    .required("Boom Lift Transport Price is required")
    .min(0, "Must be greater than 0"),
  scissor_lift_price: Yup.number()
    .required("Scissor Lift Transport Price is required")
    .min(0, "Must be greater than 0"),
  area_codes: Yup.array().min(1, "At least one Prefix is required"),
});

const CreateZone = () => {
  // Use
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { _id } = useParams();
  const { t } = useTranslation();

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );

  const {
    getDeliveryPricingLoading,
    authLoading,
    createZoneLoading,
    getCountryLoading,
    getPrefixLoading,
  } = useAppSelector((state: RootState) => state?.loading);
  const { countryList, prefixList, prefixCount } = useAppSelector(
    (state: RootState) => state?.country,
  );

  // State
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState<ZoneData>({
    zone_name: "",
    country_id: "",
    dispatch_to_delivery_allowance: "",
    pallet_price: "",
    half_price: "",
    full_price: "",
    mezzanine_half_price: "",
    mezzanine_full_price: "",
    fork_lift_price: "",
    boom_lift_price: "",
    scissor_lift_price: "",
    area_codes: [],
    box_prices: {},
  });
  const [parcelBoxes, setParcelBoxes] = useState<ParcelBoxItem[]>([]);
  const [parcelBoxesLoading, setParcelBoxesLoading] = useState(false);
  const [parcelBoxesLoaded, setParcelBoxesLoaded] = useState(false);
  const [prefixLoaded, setPrefixLoaded] = useState(false);
  const parcelBoxesRef = useRef<ParcelBoxItem[]>([]);
  const [prefixSearch, setPrefixSearch] = useState("");
  const debouncedPrefixSearch = useDebounce(prefixSearch, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [prefixLimit, setPrefixLimit] = useState(150);
  const [selectedPrefixObjects, setSelectedPrefixObjects] = useState<any[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const hasMore = (prefixList || []).length < prefixCount;

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedPrefixSearch]);

  useEffect(() => {
    if (prefixList && prefixList.length > 0) {
      setSelectedPrefixObjects((prev) => {
        const newObjects = prefixList.filter(
          (p: any) =>
            !prev.find(
              (s: any) => (s._id || s.area_id) === (p._id || p.area_id),
            ),
        );
        return [...prev, ...newObjects];
      });
    }
  }, [prefixList]);

  useEffect(() => {
    dispatch(GetCountryListAPI() as any);
  }, [dispatch]);

  useEffect(() => {
    setParcelBoxesLoading(true);
    dispatch(
      getParcelBoxesAPI({
        filters: { limit: 100 },
        onSuccess: (data: any) => {
          const boxes: ParcelBoxItem[] = data?.parcel_boxes || [];
          setParcelBoxes(boxes);
          parcelBoxesRef.current = boxes;
          setEditData((prev) => {
            const updatedBoxPrices = { ...prev.box_prices };
            boxes.forEach((box) => {
              if (updatedBoxPrices[box._id] === undefined) {
                updatedBoxPrices[box._id] = box.default_price ?? 0;
              }
            });
            return { ...prev, box_prices: updatedBoxPrices };
          });
          setParcelBoxesLoading(false);
          setParcelBoxesLoaded(true);
        },
        onError: () => {
          setParcelBoxesLoading(false);
        },
      }) as any
    );
  }, [dispatch]);

  useEffect(() => {
    if (_id) {
      setLoading(true);
      axiosInstance
        .get(`/delivery-zone-price/${_id}`)
        .then((response) => {
          let result = responseDataFormate(response.data);
          if (result?.success && result?.data) {
            const data = result.data;

            const savedBoxes: { [key: string]: number | string } = {};
            (data.box_prices || []).forEach((b: any) => {
              const bId = String(b.box_id?._id || b.box_id || b._id);
              if (bId) {
                savedBoxes[bId] = b.price;
              }
            });

            const mergedBoxPrices: { [key: string]: number | string } = {};
            parcelBoxesRef.current.forEach((box) => {
              mergedBoxPrices[box._id] = box.default_price ?? 0;
            });
            Object.assign(mergedBoxPrices, savedBoxes);

            setEditData({
              zone_name: data.zone_name,
              country_id: data.country_id,
              dispatch_to_delivery_allowance:
                data.dispatch_to_delivery_allowance !== undefined &&
                data.dispatch_to_delivery_allowance !== null
                  ? data.dispatch_to_delivery_allowance
                  : "",
              pallet_price: data.normal_price?.pallet_price ?? "",
              full_price: data.normal_price?.tailer?.full_price ?? "",
              half_price: data.normal_price?.tailer?.half_price ?? "",
              mezzanine_full_price:
                data.normal_price?.mezzanine_tailer?.mezzanine_full_price ?? "",
              mezzanine_half_price:
                data.normal_price?.mezzanine_tailer?.mezzanine_half_price ?? "",
              fork_lift_price: data.normal_price?.fork_lift_price ?? "",
              boom_lift_price: data.normal_price?.boom_lift_price ?? "",
              scissor_lift_price: data.normal_price?.scissor_lift_price ?? "",
              area_codes:
                data.area_codes?.map((a: any) => a.area_id || a._id) || [],
              box_prices: mergedBoxPrices,
            });
            setSelectedPrefixObjects(data.area_codes || []);
            setSelectedCountryId(data.country_id);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [_id]);

  const fetchPrefixes = (
    country_id: string,
    search = "",
    page = 1,
    limit = 150,
  ) => {
    dispatch(
      GetPrefixListAPI(
        country_id,
        _id ? _id : null,
        search,
        page,
        limit,
      ) as any,
    ).then(() => {
      setPrefixLoaded(true);
    }).catch(() => {
      // even on error or completion
    });
  };

  useEffect(() => {
    if (selectedCountryId) {
      fetchPrefixes(
        selectedCountryId,
        debouncedPrefixSearch,
        currentPage,
        prefixLimit,
      );
    }
  }, [selectedCountryId, _id, debouncedPrefixSearch, currentPage, prefixLimit]);

  const handleSave = async (values: any) => {
    // Map selected prefixes to area_codes format expected by API
    const area_codes = (selectedPrefixObjects || [])
      .filter((item: any) =>
        values.area_codes.includes(item._id || item.area_id),
      )
      .map((item: any) => ({
        area_id: item._id || item.area_id,
        prefix: item.prefix,
        area_name: item.area_name,
        cross_border: item.cross_border || false,
      }));

    const box_prices = parcelBoxes.map((box) => {
      const rawPrice = values?.box_prices?.[box._id];
      const parsedPrice =
        rawPrice !== undefined && rawPrice !== ""
          ? Number(rawPrice)
          : (box.default_price ?? 0);
      return {
        box_id: box._id,
        name: box.name,
        dimensions_text: box.dimensions_text || "",
        price: isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice),
      };
    });

    const payload = {
      country_id: values.country_id,
      zone_name: values.zone_name,
      dispatch_to_delivery_allowance:
        values.dispatch_to_delivery_allowance !== "" &&
        values.dispatch_to_delivery_allowance !== null &&
        values.dispatch_to_delivery_allowance !== undefined
          ? Number(values.dispatch_to_delivery_allowance)
          : 0,
      area_codes: area_codes,
      pallet_price: Number(values.pallet_price) || 0,
      full_price: Number(values.full_price) || 0,
      half_price: Number(values.half_price) || 0,
      mezzanine_full_price: Number(values.mezzanine_full_price) || 0,
      mezzanine_half_price: Number(values.mezzanine_half_price) || 0,
      fork_lift_price: Number(values.fork_lift_price) || 0,
      boom_lift_price: Number(values.boom_lift_price) || 0,
      scissor_lift_price: Number(values.scissor_lift_price) || 0,
      box_prices: box_prices,
    };

    if (_id) {
      dispatch(
        UpdateDeliveryZoneAPI(_id, payload, () => {
          navigate("/master-data/delivery-pricing");
        }) as any,
      );
    } else {
      dispatch(
        CreateDeliveryZoneAPI(payload, () => {
          navigate("/master-data/delivery-pricing");
        }) as any,
      );
    }
  };

  return USER_PERMISSIONS?.READ ? (
    loading ? (
      <FullScreenLoader />
    ) : (
      <div className="page-content h-100 px-40 py-40">
        <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-sm-center align-items-start mb-md-4 mb-3">
          <h2 className="text-black fs-36 fw-bolder page-title mb-0">
            <span
              onClick={() => navigate(-1)} // ✅ Wired up Back action
              className="cursor-pointer svg-24 me-2"
            >
              {svg?.backArrow}
            </span>
            {_id ? "Modify" : "Create"} Zone
          </h2>
          <div className="d-md-flex d-none justify-content-end align-items-center gap-3">
            <button
              className="btn btn-lg btn-outline-secondary mob-btn"
              onClick={() => navigate(-1)}
            >
              {t("button.cancel")}
            </button>
            <button
              type="submit"
              form="submitCreateZone"
              disabled={
                createZoneLoading ||
                (_id ? !parcelBoxesLoaded || !prefixLoaded : false)
              }
              className="btn btn-lg btn-primary mob-btn"
            >
              {createZoneLoading ? <BtnLoader /> : _id ? "UPDATE" : "CREATE"}
            </button>
          </div>
        </div>

        <div
          className="card page-table-card border-0 shadow rounded-4 overflow-y-auto"
          id="scrollableDiv"
        >
          <div className="card-body p-md-4 p-3">
            <Formik
              enableReinitialize={true}
              initialValues={{
                zone_name: _id ? editData.zone_name : "",
                country_id: _id ? editData.country_id : "",
                dispatch_to_delivery_allowance: _id
                  ? (editData.dispatch_to_delivery_allowance ?? "")
                  : "",
                pallet_price: _id ? editData.pallet_price : "",
                half_price: _id ? editData.half_price : "",
                full_price: _id ? editData.full_price : "",
                mezzanine_half_price: _id ? editData.mezzanine_half_price : "",
                mezzanine_full_price: _id ? editData.mezzanine_full_price : "",
                fork_lift_price: _id ? editData.fork_lift_price : "",
                boom_lift_price: _id ? editData.boom_lift_price : "",
                scissor_lift_price: _id ? editData.scissor_lift_price : "",
                area_codes: _id ? editData.area_codes : [],
                box_prices: editData.box_prices || {},
              }}
              validationSchema={schema}
              onSubmit={handleSave}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
                setFieldValue,
              }: any) => {
                return (
                  <Form
                    id="submitCreateZone"
                    onSubmit={handleSubmit}
                    className="page-form"
                  >
                    <div className="row gy-3">
                      <div className="col-xl-4 col-md-6 col-12">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            Zone Name
                          </label>
                          <Field
                            name="zone_name"
                            className="form-control form-control-lg"
                            value={values?.zone_name}
                            onBlur={handleBlur}
                            placeholder={"Enter Zone Name"}
                            as={Input}
                            onChange={(e: any) => {
                              setFieldValue("zone_name", e.target.value);
                            }}
                          />
                          {errors?.zone_name && touched?.zone_name && (
                            <div className="text-danger fs-8">
                              {errors?.zone_name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xl-4 col-md-6 col-12">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            Country
                          </label>
                          <Select
                            placeholder="Search Country"
                            allowClear
                            showSearch
                            filterOption={(input: string, option: any) =>
                              (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase().trim())
                            }
                            value={values?.country_id || null}
                            onClear={() => setFieldValue("area_codes", [])}
                            onChange={(value: any) => {
                              if (
                                values?.country_id &&
                                value &&
                                values?.country_id !== value &&
                                values?.area_codes?.length
                              ) {
                                const oldCountryName =
                                  countryList.find(
                                    (c: any) => c._id === values?.country_id,
                                  )?.country_name || values?.country_id;
                                const newCountryName =
                                  countryList.find((c: any) => c._id === value)
                                    ?.country_name || value;
                                Modal.confirm({
                                  title: `Are you sure you want to change the country from ${oldCountryName} to ${newCountryName}?`,
                                  className: "confirm-country-change-modal",
                                  okButtonProps: {
                                    className: "btn btn-primary fs-14",
                                  },
                                  cancelButtonProps: {
                                    className:
                                      "btn btn-outline-secondary fs-14",
                                  },
                                  onOk: () => {
                                    setFieldValue("country_id", value);
                                    setFieldValue("area_codes", []);
                                    setSelectedCountryId(value);
                                    setCurrentPage(1);
                                  },
                                  onCancel: () => {
                                    // Do nothing
                                  },
                                });
                              } else {
                                setFieldValue("country_id", value);
                                if (!value) {
                                  setFieldValue("area_codes", []);
                                  dispatch({ type: "CLEAR_PREFIX_DATA" });
                                  setSelectedCountryId("");
                                } else {
                                  setSelectedCountryId(value);
                                  setCurrentPage(1);
                                }
                              }
                            }}
                            options={countryList.map((c: any) => ({
                              label: c.country_name,
                              value: c._id,
                            }))}
                            suffixIcon={svg?.selectDownArrow}
                            className="w-100"
                          />
                          {errors?.country_id && touched?.country_id && (
                            <div className="text-danger fs-8">
                              {errors?.country_id as string}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xl-4 col-md-6 col-12">
                        <div className="form-item">
                          <label
                            className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                            title="Dispatch to Delivery Allowance (Days)"
                          >
                            Dispatch to Delivery Allowance (Days)
                          </label>
                          <Input
                            name="dispatch_to_delivery_allowance"
                            type="number"
                            min={0}
                            placeholder="Enter Allowance (Days)"
                            value={values?.dispatch_to_delivery_allowance}
                            onBlur={handleBlur}
                            onChange={(e: any) => {
                              setFieldValue(
                                "dispatch_to_delivery_allowance",
                                e.target.value,
                              );
                            }}
                            className="form-control form-control-lg form-days-input"
                            prefix={<span className="text-white">Days</span>}
                          />
                          {errors?.dispatch_to_delivery_allowance &&
                            touched?.dispatch_to_delivery_allowance && (
                              <div className="text-danger fs-8">
                                {
                                  errors?.dispatch_to_delivery_allowance as string
                                }
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="col-xl-4 col-sm-4 col-12">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            Pallet Price
                          </label>
                          <Input
                            name="pallet_price"
                            type="number"
                            placeholder="Enter Pallet Price"
                            value={values?.pallet_price}
                            onChange={handleChange}
                            className="form-control form-control-lg amountInput"
                            prefix={<span className="text-white">£</span>}
                          />
                          {errors.pallet_price && touched.pallet_price && (
                            <span className="text-danger">
                              {errors.pallet_price as React.ReactNode}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-xl-4 col-sm-4 col-12">
                        <div className="form-item">
                          <label
                            className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                            title="Half Load (Trailer) Price"
                          >
                            Half Load (Trailer) Price
                          </label>
                          <Input
                            name="half_price"
                            type="number"
                            placeholder="Enter Half Load (Trailer) Price"
                            value={values?.half_price}
                            onChange={handleChange}
                            className="form-control form-control-lg amountInput"
                            prefix={<span className="text-white">£</span>}
                          />
                          {errors.half_price && touched.half_price && (
                            <span className="text-danger">
                              {errors.half_price as React.ReactNode}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-xl-4 col-sm-4 col-12">
                        <div className="form-item">
                          <label
                            className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                            title="Full Load (Trailer) Price"
                          >
                            Full Load (Trailer) Price
                          </label>
                          <Input
                            name="full_price"
                            type="number"
                            placeholder="Enter Full Load (Trailer) Price"
                            value={values?.full_price}
                            onChange={handleChange}
                            className="form-control form-control-lg amountInput"
                            prefix={<span className="text-white">£</span>}
                          />
                          {errors.full_price && touched.full_price && (
                            <span className="text-danger">
                              {errors.full_price as React.ReactNode}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="row g-3">
                          <div className="col-xl-2 col-md-4 col-12">
                            <div className="form-item">
                              <label
                                className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                title="Fork Lift Transport Price"
                              >
                                Fork Lift Transport Price
                              </label>
                              <Input
                                name="fork_lift_price"
                                type="number"
                                placeholder="Fork Lift Transport Price"
                                value={values?.fork_lift_price}
                                onChange={handleChange}
                                className="form-control form-control-lg amountInput"
                                prefix={<span className="text-white">£</span>}
                              />
                              {errors.fork_lift_price &&
                                touched.fork_lift_price && (
                                  <span className="text-danger">
                                    {errors.fork_lift_price as React.ReactNode}
                                  </span>
                                )}
                            </div>
                          </div>
                          <div className="col-xl-2 col-md-4 col-12">
                            <div className="form-item">
                              <label
                                className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                title="Boom Lift Transport Price"
                              >
                                Boom Lift Transport Price
                              </label>
                              <Input
                                name="boom_lift_price"
                                type="number"
                                placeholder="Boom Lift Transport Price"
                                value={values?.boom_lift_price}
                                onChange={handleChange}
                                className="form-control form-control-lg amountInput"
                                prefix={<span className="text-white">£</span>}
                              />
                              {errors.boom_lift_price &&
                                touched.boom_lift_price && (
                                  <span className="text-danger">
                                    {errors.boom_lift_price as React.ReactNode}
                                  </span>
                                )}
                            </div>
                          </div>
                          <div className="col-xl-2 col-md-4 col-12">
                            <div className="form-item">
                              <label
                                className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                title="Scissor Lift Transport Price"
                              >
                                Scissor Lift Transport Price
                              </label>
                              <Input
                                name="scissor_lift_price"
                                type="number"
                                placeholder="Scissor Lift Transport Price"
                                value={values?.scissor_lift_price}
                                onChange={handleChange}
                                className="form-control form-control-lg amountInput"
                                prefix={<span className="text-white">£</span>}
                              />
                              {errors.scissor_lift_price &&
                                touched.scissor_lift_price && (
                                  <span className="text-danger">
                                    {
                                      errors.scissor_lift_price as React.ReactNode
                                    }
                                  </span>
                                )}
                            </div>
                          </div>
                          <div className="col-xl-3 col-md-6 col-12">
                            <div className="form-item">
                              <label
                                className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                title="Mezzanine Half Load (Trailer) Price"
                              >
                                Mezzanine Half Load (Trailer) Price
                              </label>
                              <Input
                                name="mezzanine_half_price"
                                type="number"
                                placeholder="Mezzanine Half Load Price"
                                value={values?.mezzanine_half_price}
                                onChange={handleChange}
                                className="form-control form-control-lg amountInput"
                                prefix={<span className="text-white">£</span>}
                              />
                              {errors.mezzanine_half_price &&
                                touched.mezzanine_half_price && (
                                  <span className="text-danger">
                                    {
                                      errors.mezzanine_half_price as React.ReactNode
                                    }
                                  </span>
                                )}
                            </div>
                          </div>
                          <div className="col-xl-3 col-md-6 col-12">
                            <div className="form-item">
                              <label
                                className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                title="Mezzanine Full Load (Trailer) Price"
                              >
                                Mezzanine Full Load (Trailer) Price
                              </label>
                              <Input
                                name="mezzanine_full_price"
                                type="number"
                                placeholder="Mezzanine Full Load Price"
                                value={values?.mezzanine_full_price}
                                onChange={handleChange}
                                className="form-control form-control-lg amountInput"
                                prefix={<span className="text-white">£</span>}
                              />
                              {errors.mezzanine_full_price &&
                                touched.mezzanine_full_price && (
                                  <span className="text-danger">
                                    {
                                      errors.mezzanine_full_price as React.ReactNode
                                    }
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {parcelBoxesLoading ? (
                        <div className="col-12 text-center p-3">
                          <Spin />
                        </div>
                      ) : parcelBoxes && parcelBoxes.length > 0 ? (
                        <div className="col-12">
                          <div className="form-item">
                            <div className="d-flex align-items-center mb-2">
                              <label className="custom-label ff fs-5 mb-0">
                                Parcel Delivery Prices
                              </label>
                            </div>
                            <div className="row g-3">
                              {parcelBoxes.map((box: ParcelBoxItem) => (
                                <div
                                  className="col-xl-2 col-md-4 col-12"
                                  key={box._id}
                                >
                                  <div className="form-item">
                                    <label
                                      className="custom-label mb-1 ff text-nowrap text-truncate d-block"
                                      title={box.name}
                                    >
                                      {box.name}
                                    </label>
                                    <Input
                                      type="number"
                                      min={0}
                                      placeholder={`Enter ${box.name} Price`}
                                      value={
                                        values?.box_prices?.[box._id] !== undefined
                                          ? values?.box_prices?.[box._id]
                                          : (box.default_price ?? "")
                                      }
                                      onChange={(e) => {
                                        setFieldValue(
                                          `box_prices.${box._id}`,
                                          e.target.value
                                        );
                                      }}
                                      className="form-control form-control-lg amountInput"
                                      prefix={
                                        <span className="text-white">£</span>
                                      }
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="col-12">
                        <div className="form-item">
                          <div className="d-flex justify-content-between justify-content-md-start align-items-center mb-2 gap-3">
                            <label className="custom-label ff fs-6 mb-0">
                              Prefix
                            </label>
                            <div
                              className="pageSearch mw-100 position-relative d-inline-block"
                              style={{ width: "250px" }}
                            >
                              <input
                                type="text"
                                value={prefixSearch}
                                onChange={(e) =>
                                  setPrefixSearch(e.target.value)
                                }
                                className="form-control form-control-lg"
                                placeholder="Search Prefix"
                                disabled={!values?.country_id}
                              />
                              {prefixSearch?.length > 0 ? (
                                <span
                                  className="cursor-pointer"
                                  onClick={() => setPrefixSearch("")}
                                >
                                  {svg?.searchClear}
                                </span>
                              ) : (
                                <span>{svg?.search}</span>
                              )}
                            </div>
                          </div>

                          <div className="mt-3">
                            {getPrefixLoading && currentPage === 1 ? (
                              <div className="col-12 text-center p-3">
                                <Spin />
                              </div>
                            ) : (
                              <InfiniteScroll
                                dataLength={(prefixList || []).length}
                                next={() => setCurrentPage((prev) => prev + 1)}
                                hasMore={hasMore}
                                loader={
                                  <div className="text-center p-3">
                                    <Spin />
                                  </div>
                                }
                                scrollableTarget="scrollableDiv"
                                className="row"
                              >
                                {(prefixList || []).map((item: any) => (
                                  <div
                                    className="col-xl-2 col-lg-3 col-md-4 col-6 mb-2"
                                    key={
                                      item._id || item.area_id || item.prefix
                                    }
                                  >
                                    <Checkbox
                                      checked={values?.area_codes?.includes(
                                        item._id || item.area_id,
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFieldValue("area_codes", [
                                            ...values.area_codes,
                                            item._id || item.area_id,
                                          ]);
                                        } else {
                                          setFieldValue(
                                            "area_codes",
                                            values.area_codes.filter(
                                              (id: string) =>
                                                id !==
                                                (item._id || item.area_id),
                                            ),
                                          );
                                        }
                                      }}
                                    >
                                      {item.prefix} ({item.area_name}){" "}
                                    </Checkbox>
                                  </div>
                                ))}
                              </InfiniteScroll>
                            )}
                          </div>
                          {errors?.area_codes && touched?.area_codes && (
                            <div className="text-danger mt-1 fs-8">
                              {errors.area_codes as string}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="d-flex justify-content-center justify-content-md-end align-items-center gap-3 mt-3 mt-md-4">
          <button
            className="btn btn-lg btn-outline-secondary mob-btn"
            onClick={() => navigate(-1)}
          >
            {t("button.cancel")}
          </button>
          <button
            type="submit"
            form="submitCreateZone"
            disabled={
              createZoneLoading ||
              (_id ? !parcelBoxesLoaded || !prefixLoaded : false)
            }
            className="btn btn-lg btn-primary mob-btn"
          >
            {createZoneLoading ? <BtnLoader /> : _id ? "UPDATE" : "CREATE"}
          </button>
        </div>
      </div>
    )
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default CreateZone;
